import React from "react";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";
import { colors, gradients, shadows } from "./colors";

export type ButtonVariant =
  | "primary"
  | "ghost"
  | "secondary"
  | "outlined"
  | "text";

export interface ButtonProps extends Omit<MuiButtonProps, "variant"> {
  variant?: ButtonVariant;
  loading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  loading = false,
  children,
  sx,
  disabled,
  ...props
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          backgroundColor: colors.primary[500],
          color: colors.neutral.white,
          fontFamily: "Outfit, sans-serif",
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none" as const,
          padding: "11px 12px",
          borderRadius: "100px",
          "&:hover": {
            background: gradients.primaryHover,
            boxShadow: shadows.primaryHover,
          },
          "&:disabled": {
            background: colors.background[700],
            boxShadow: "none",
            color: colors.neutral[800],
            cursor: "not-allowed",
          },
        };
      case "ghost":
        return {
          background: gradients.ghost,
          color: colors.neutral.white,
          fontFamily: "Outfit, sans-serif",
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none" as const,
          padding: "11px 32px",
          borderRadius: "100px",
          minHeight: "44px",
          "&:hover": {
            background: gradients.ghostHover,
            boxShadow: `0 0 50px 0 ${colors.button.ghostHoverGlowOuter}, 0 0 20px 0 ${colors.button.ghostHoverGlowMid}, 3px 2px 20px 0 ${colors.button.ghostHoverShadowInner} inset`,
          },
          "&:active": {
            background: colors.primary[700],
            boxShadow: "none",
          },
          "&:disabled": {
            background: colors.background[700],
            color: colors.neutral[800],
          },
        };
      case "secondary":
        return {
          backgroundColor: "transparent",
          color: colors.primary[500],
          fontFamily: "Outfit, sans-serif",
          fontSize: "1rem",
          fontWeight: 400,
          textTransform: "none" as const,
          padding: "0.5rem 1rem",
          borderRadius: "8px",
          border: `1px solid ${colors.primary[500]}`,
          minHeight: "44px",
          "&:hover": {
            backgroundColor: colors.neutral[300],
            color: colors.background[900],
            borderColor: colors.neutral[300],
          },
        };
      case "outlined":
        return {
          backgroundColor: "transparent",
          color: colors.neutral.white,
          fontFamily: "Outfit, sans-serif",
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none" as const,
          padding: "11px 32px",
          borderRadius: "100px",
          border: `1px solid ${colors.neutral[900]}`,
          height: "50px",
          "&:hover": {
            borderColor: colors.neutral[700],
            backgroundColor: colors.button.outlinedHoverBg,
            boxShadow: `0 0 7px 0 ${colors.button.outlinedHoverShadow} inset`,
          },
          "&:active": {
            backgroundColor: colors.button.whiteLowOpacity,
          },
        };
      case "text":
        return {
          backgroundColor: "transparent",
          color: colors.neutral.white,
          fontFamily: "Outfit, sans-serif",
          fontSize: "1rem",
          fontWeight: 400,
          textTransform: "none" as const,
          padding: "0.5rem 1rem",
          borderRadius: "8px",
          minHeight: "44px",
          "&:hover": {
            backgroundColor: colors.neutral[300],
            color: colors.background[900],
          },
        };
      default:
        return {};
    }
  };

  return (
    <MuiButton
      {...props}
      disabled={disabled || loading}
      fullWidth={props.fullWidth}
      disableRipple
      sx={{
        ...getVariantStyles(),
        ...sx,
      }}
      startIcon={
        loading ? (
          <CircularProgress size={20} sx={{ color: "inherit" }} />
        ) : (
          props.startIcon
        )
      }
    >
      {children}
    </MuiButton>
  );
};

export default Button;
